import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, MapPin, CalendarClock, UsersRound, ClipboardCheck, PiggyBank, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export function ZonesModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'list',
      title: 'Quản lý Không gian',
      description: 'Danh sách và thông tin chi tiết các phân khu, diện tích, công năng sử dụng.',
      icon: <LayoutGrid className="w-6 h-6" />,
      color: 'blue',
      path: '/zones/list',
      stats: '24 Phân khu',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'map',
      title: 'Bản đồ Trực quan',
      description: 'Định vị GPS, sơ đồ quy hoạch GIS và trạng thái thi công thực tế trên bản đồ.',
      icon: <MapPin className="w-6 h-6" />,
      color: 'emerald',
      path: '/zones/map',
      stats: 'Định vị GIS',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'maintenance',
      title: 'Lịch trình Bảo trì',
      description: 'Thiết lập và theo dõi kế hoạch chăm sóc định kỳ cho từng khu vực cảnh quan.',
      icon: <CalendarClock className="w-6 h-6" />,
      color: 'amber',
      path: '/maintenance/plans',
      stats: '15 Kế hoạch',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'resources',
      title: 'Phân bổ Nguồn lực',
      description: 'Kiểm soát nhân sự, máy móc thiết bị đang hoạt động tại từng phân khu dự án.',
      icon: <UsersRound className="w-6 h-6" />,
      color: 'purple',
      path: '/resources',
      stats: 'Điều động',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'inspections',
      title: 'Kiểm tra & Đánh giá',
      description: 'Quản lý các đợt đi tuần, nghiệm thu chất lượng cây xanh, cảnh quan.',
      icon: <ClipboardCheck className="w-6 h-6" />,
      color: 'indigo',
      path: '/construction/inspections',
      stats: 'Nghiệm thu',
      gradient: 'from-indigo-500 to-violet-500',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    },
    {
      id: 'costs',
      title: 'Chi phí Phân bổ',
      description: 'Báo cáo chi phí vật tư, nhân công đã phân bổ cho từng phân khu cụ thể.',
      icon: <PiggyBank className="w-6 h-6" />,
      color: 'slate',
      path: '/construction/cost-tracking',
      stats: 'Tài chính',
      gradient: 'from-slate-500 to-gray-600',
      bgLight: 'bg-slate-100',
      textDark: 'text-slate-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-blue-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-blue-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
